import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { TrendingUp, ShieldCheck, Check, X } from 'lucide-react';

const validatePassword = (password) => {
  const requirements = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(password),
  };
  return requirements;
};

export default function ChangePasswordPage() {
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const requirements = validatePassword(newPw);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPw !== confirm) {
      setError('Passwords do not match');
      return;
    }
    
    // Check if all requirements are met
    const allRequirementsMet = Object.values(requirements).every(req => req);
    if (!allRequirementsMet) {
      setError('Please meet all password requirements');
      return;
    }
    
    setLoading(true);
    try {
      await changePassword(current, newPw);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="h-10 w-10 rounded-md bg-primary flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">TheMoneyMap</h1>
        </div>

        <Card className="border border-border shadow-none">
          <CardHeader className="text-left">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <CardTitle className="text-xl tracking-tight font-['Outfit']">Update Password</CardTitle>
            </div>
            <CardDescription>You must change your password before continuing.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20" data-testid="change-pw-error">
                  {error}
                </div>
              )}
              <div className="space-y-2 text-left">
                <Label>Current Password</Label>
                <Input
                  data-testid="current-password-input"
                  type="password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2 text-left">
                <Label>New Password</Label>
                <Input
                  data-testid="new-password-input"
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  required
                />
                {newPw && (
                  <div className="mt-3 space-y-2 text-xs">
                    <p className="font-medium text-muted-foreground">Password must contain:</p>
                    <div className="space-y-1">
                      <div className={`flex items-center gap-2 ${requirements.length ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {requirements.length ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>At least 8 characters</span>
                      </div>
                      <div className={`flex items-center gap-2 ${requirements.uppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {requirements.uppercase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>One uppercase letter (A-Z)</span>
                      </div>
                      <div className={`flex items-center gap-2 ${requirements.lowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {requirements.lowercase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>One lowercase letter (a-z)</span>
                      </div>
                      <div className={`flex items-center gap-2 ${requirements.number ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {requirements.number ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>One number (0-9)</span>
                      </div>
                      <div className={`flex items-center gap-2 ${requirements.special ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {requirements.special ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>One special character (!@#$%^&*etc.)</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2 text-left">
                <Label>Confirm New Password</Label>
                <Input
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="change-pw-submit-btn">
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
